import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheExpiryConstants, CacheKeyConstants, CacheService, DriverDocumentConstants, FileData, isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import moment from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DocumentReviewItem, DriverDocument, DriverDocumentType, TncDriverProfile } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class DriverDocumentService extends UzurvRestService {
  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Retrieves a TNC driver profile for the requested driver. Caches this value by default, and can
   * be overridden by passing a flag
   *
   * @param driverId
   * @param overrideCache
   */
  getTncProileForDriver(driverId: number, overrideCache = false): Observable<TncDriverProfile> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.TNC_DRIVER_PROFILE);

    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), TncDriverProfile));
    }

    return this.get('drivers/' + driverId + '/profile').pipe(
      map(response => {
        this.cacheService.cacheData(
          CacheKeyConstants.TNC_DRIVER_PROFILE,
          JSON.stringify(response),
          CacheExpiryConstants.DOMAIN_CACHE_EXPIRY
        );
        return Deserialize(response, TncDriverProfile);
      })
    );
  }

  /**
   * Retrieves the requred document types and caches them by default
   *
   * @param driverId
   * @param overrideCach
   */
  getDriverDocumentTypes(driverId: number, overrideCache = false): Observable<Array<DriverDocumentType>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.DRIVER_DOCUMENT_TYPES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), DriverDocumentType));
    }

    return this.get('driverOnboarding/drivers/' + driverId + '/requiredDocumentTypes/').pipe(
      map(response => {
        this.cacheService.cacheData(
          CacheKeyConstants.DRIVER_DOCUMENT_TYPES,
          JSON.stringify(response),
          CacheExpiryConstants.DOMAIN_CACHE_EXPIRY
        );
        return Deserialize(response, DriverDocumentType);
      })
    );
  }

  /**
   * Retrieves documents for a given driver
   *
   * @param driverId
   */
  getDocumentsForDriver(driverId: number): Observable<Array<DriverDocument>> {
    return this.get('driverOnboarding/drivers/' + driverId + '/documents/').pipe(map(documents => Deserialize(documents, DriverDocument)));
  }

  /**
   * Resends a request for a driver to digitally sign a document with a given documentTypeId
   *
   * @param driverId
   * @param documentTypeId
   */
  resendSignatureRequest(driverId: number, documentTypeId: number): Observable<any> {
    return this.get('driverOnboarding/drivers/' + driverId + '/resendSignatureRequest/?documentTypeId=' + documentTypeId);
  }

  /**
   * Retrieves the next driver for document approval
   *
   * @permission driver_onboarding_management
   */
  getNextDriverForDocumentReview(): Observable<DocumentReviewItem> {
    return this.get('driverOnboarding/documentReviewItems/').pipe(map(response => Deserialize(response, DocumentReviewItem)));
  }

  /**
   * Gets a specific document given the driver ID and the document ID
   *
   * @param driverId
   * @param documentId
   */
  getDocumentById(driverId: number, documentId: number): Observable<DriverDocument> {
    return this.get('driverOnboarding/drivers/' + driverId + '/documents/' + documentId).pipe(
      map(response => Deserialize(response, DriverDocument))
    );
  }

  /**
   * Gets a File for given driver ID and document ID
   *
   * @param driverId
   * @param documentId
   */
  getFileByDriverIdAndDocumentId(driverId: number, documentId: number): Observable<FileData> {
    return this.get('driverOnboarding/drivers/' + driverId + '/documents/' + documentId + '/file').pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Creates a submission of a driver document for a given driver by their ID
   *
   * @deprecated
   *
   * @param driverId
   * @param driverDocument
   */
  createDriverDocument(driverId: number, driverDocument: DriverDocument): Observable<DriverDocument> {
    return this.post('driverOnboarding/drivers/' + driverId + '/documents/', Serialize(driverDocument)).pipe(
      map(response => Deserialize(response, DriverDocument))
    );
  }

  /**
   * Complete the driver documnet approval process by PUTing the documents to the server with the updated
   * approved/rejected flags
   *
   * @param documentReviewItem
   * @permission driver_training_management
   */
  completeDocumentReviewForDriver(documentReviewItem: DocumentReviewItem): Observable<Array<DriverDocument>> {
    return this.put(
      'driverOnboarding/drivers/' + documentReviewItem.driver.id + '/documentReviewItems/',
      Serialize(documentReviewItem)
    ).pipe(map(response => Deserialize(response, DriverDocument)));
  }

  getDriverDocumentStatus(driverDocument: DriverDocument): DriverDocumentConstants.Status {
    if (driverDocument.rejected) {
      return DriverDocumentConstants.Status.Rejected;
    } else if (this.isDriverDocumentExpiring(driverDocument)) {
      return DriverDocumentConstants.Status.Expiring;
    } else if (this.isDriverDocumentExpired(driverDocument)) {
      return DriverDocumentConstants.Status.Expired;
    } else if (driverDocument.approved && driverDocument.active) {
      return DriverDocumentConstants.Status.Approved;
    } else if (driverDocument.active && !driverDocument.approved) {
      return DriverDocumentConstants.Status.Pending;
    }
    return null;
  }

  isDriverDocumentExpiring(driverDocument: DriverDocument): boolean {
    if (driverDocument.expirationDate == null) {
      return false;
    }

    return moment(driverDocument.expirationDate).isBetween(
      moment(),
      moment().add(DriverDocumentConstants.DAYS_BEFORE_EXPIRATION_TO_FLAG, 'days')
    );
  }

  isDriverDocumentExpired(driverDocument: DriverDocument): boolean {
    if (driverDocument.expirationDate == null) {
      return false;
    }
    return moment(driverDocument.expirationDate).isBefore(moment());
  }
}
